import backgroundBenchmark from "@images/backgroundBenchmark.png"
import backgroundProfile from "@images/backgroundProfile.png"
import { ExploreCardFooterAvatar } from "@modules/explore/components/exploreCard/exploreCardFooterAvatar"
import { ExploreCardFooterChrono } from "@modules/explore/components/exploreCard/exploreCardFooterChrono"
import { ExploreCardHeader } from "@modules/explore/components/exploreCard/exploreCardHeader"
import { ExploreCardTitle } from "@modules/explore/components/exploreCard/exploreCardTitle"
import {
  ExploreBenchmarkItemEntity,
  ExploreBoxItemEntity,
  ExploreProfileItemEntity,
  ExploreProgramBundleItemEntity,
  ExploreProgramItemEntity,
} from "@modules/explore/entities/exploreEntity"
import { ExploreContentType, ExploreFilter } from "@modules/explore/exploreTypes"
import { ProgramTypeOf } from "@modules/programs/programTypes"
import { HUImage } from "@modules/types/imageTypes"
import { HUBackgroundImageView } from "@modules/ui/components/huBackgroundImageView"
import { useMemo, useState } from "react"
import styled from "styled-components"
type ExploreCardProps = {
  item:
    | ExploreBenchmarkItemEntity
    | ExploreProgramItemEntity
    | ExploreProgramBundleItemEntity
    | ExploreBoxItemEntity
    | ExploreProfileItemEntity
  onViewDetailsClick: (itemId: string, typeOf: ExploreContentType, programType?: ProgramTypeOf) => void
  isSelected: boolean
  resizable?: boolean
  backgroundImage?: string
  disabled?: boolean
}

export const ExploreCard: React.FC<ExploreCardProps> = ({
  item,
  onViewDetailsClick,
  isSelected,
  resizable = false,
  backgroundImage = "",
  disabled = false,
}) => {
  const [isHovered, setIsHovered] = useState(false)

  const itemImage = useMemo(() => {
    if (backgroundImage !== "") {
      return { path: backgroundImage }
    }

    switch (item._type) {
      case ExploreFilter.PROGRAM:
      case ExploreFilter.PROGRAM_BUNDLE:
        return item.image
      case ExploreFilter.BOX:
        return item.cover
      case ExploreFilter.BENCHMARK:
        return { path: backgroundBenchmark }
      case ExploreFilter.PROFILE:
        return { path: backgroundProfile }
    }
  }, [item])

  const onHoverColor = useMemo(() => {
    if (isHovered) {
      switch (item._type) {
        case ExploreFilter.PROGRAM:
        case ExploreFilter.PROGRAM_BUNDLE:
        case ExploreFilter.BOX:
          return "green-gradiant"
        case ExploreFilter.BENCHMARK:
          return "blur-benchmark"
        case ExploreFilter.PROFILE:
          return "blur-profile"
        default:
          return ""
      }
    } else {
      switch (item._type) {
        case ExploreFilter.PROGRAM:
        case ExploreFilter.PROGRAM_BUNDLE:
        case ExploreFilter.BOX:
          return "black-gradiant"
        case ExploreFilter.BENCHMARK:
        case ExploreFilter.PROFILE:
          return ""
        default:
          return ""
      }
    }
  }, [item, isHovered])

  const toggleHovered = (value: boolean) => {
    if (!disabled) {
      setIsHovered(value)
    }
  }

  return (
    <CardContainer
      image={itemImage as HUImage}
      onMouseEnter={() => toggleHovered(true)}
      onMouseLeave={() => toggleHovered(false)}
      onClick={() => {
        if (item._type) {
          const programType = "programType" in item ? item.programType : undefined
          onViewDetailsClick(item.id, item._type, programType)
        }
      }}
      className={
        (isSelected ? "selected" : "") +
        (disabled ? "" : " cursor-pointer") +
        (item._type === ExploreFilter.PROFILE ? " bg-neutralColor-100 bg-auto bg-repeat" : "")
      }
      width={resizable ? "100%" : "320px"}
      height={"300px"}
    >
      <ExploreCardHeader item={item} />
      {item._type !== ExploreFilter.PROFILE && <ExploreCardTitle item={item} />}
      {item._type !== ExploreFilter.BENCHMARK ? (
        <ExploreCardFooterAvatar item={item} />
      ) : (
        <ExploreCardFooterChrono item={item} />
      )}

      <GradientOverlay className={onHoverColor} />
    </CardContainer>
  )
}

const CardContainer = styled(HUBackgroundImageView)`
  border-radius: 33px;
  position: relative;
  padding: 0px 0px 28px 0px;
  position: relative;
  overflow: hidden;

  &.selected {
    position: relative;

    &::before {
      content: "";
      position: absolute;
      top: -1px;
      left: -1px;
      right: -1px;
      bottom: -1px;
      border-radius: 33px;
      border: 5px solid white;
      z-index: 2;
    }

    border-radius: 33px;
    border: ${({ theme }) => `2px solid ${theme.neutralColor600}`};
  }
`
const GradientOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 33px;
  border-width: 0px;
  height: 300px;
  width: 320px;
  z-index: 1;

  &.black-gradiant {
    background: linear-gradient(180deg, rgba(34, 34, 34, 0) 0%, rgba(34, 34, 34, 0.8) 99.9%);
  }
  &.green-gradiant {
    background: linear-gradient(180deg, rgba(19, 60, 64, 0) 0%, #133c40 99.9%);
  }

  &.blur-benchmark {
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(221, 242, 71, 0) 0.01%,
      rgba(221, 242, 71, 0.76) 99.9%
    );
    backdrop-filter: blur(2px);
  }
  &.blur-profile {
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(230, 230, 230, 0) 0.01%,
      rgba(230, 230, 230, 0.76) 99.9%
    );
    backdrop-filter: blur(2px);
  }
`
